import { OrderReferenceItem, SourceType } from '@/types/order-type';
import { MessageSquare, MessageCircle, PenLine, HelpCircle } from 'lucide-react';
import Iconify from '../iconify/iconify';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

const SOURCE_META: Record<
  SourceType | 'UNKNOWN',
  {
    label: string;
    badgeBg: string;
    badgeText: string;
    icon: React.ReactNode;
  }
> = {
  [SourceType.COMMENT]: {
    label: 'Facebook comment',
    badgeBg: 'bg-blue-100',
    badgeText: 'text-blue-600',
    icon: (
      <span className="relative flex items-center justify-center">
        <Iconify icon="mdi:facebook" className="h-3.5 w-3.5" />
        <MessageSquare className="absolute -bottom-1 -right-1 h-2.5 w-2.5 bg-white rounded-full" />
      </span>
    ),
  },
  [SourceType.CHAT]: {
    label: 'Facebook message',
    badgeBg: 'bg-indigo-100',
    badgeText: 'text-indigo-600',
    icon: (
      <span className="relative flex items-center justify-center">
        <Iconify icon="mdi:facebook" className="h-3.5 w-3.5" />
        <MessageCircle className="absolute -bottom-1 -right-1 h-2.5 w-2.5 bg-white rounded-full" />
      </span>
    ),
  },
  [SourceType.MANUAL]: {
    label: 'Added manually',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-600',
    icon: <PenLine className="h-3.5 w-3.5" />,
  },
  UNKNOWN: {
    label: 'Unknown source',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-400',
    icon: <HelpCircle className="h-3.5 w-3.5" />,
  },
};

function getSourceMeta(type: SourceType) {
  return SOURCE_META[type] ?? SOURCE_META.UNKNOWN;
}

export function OrderReferenceRow({ item }: { item: OrderReferenceItem }) {
  const meta = getSourceMeta(item.sourceType);

  return (
    <div className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition text-xs w-full">
      {/* Source icon badge */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={[
              'inline-flex h-7 w-7 items-center justify-center rounded-full flex-shrink-0 cursor-default',
              meta.badgeBg,
              meta.badgeText,
            ].join(' ')}
          >
            {meta.icon}
          </span>
        </TooltipTrigger>
        <TooltipContent>{meta.label}</TooltipContent>
      </Tooltip>

      {/* Message + product code */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-700 leading-snug break-words" title={item.message}>
          {item.message}
          {item.productCode && (
            <span className="ml-1.5 font-medium text-slate-400">• {item.productCode}</span>
          )}
        </p>
      </div>

      {/* Timestamp */}
      <div className="flex-shrink-0 whitespace-nowrap rounded-md bg-slate-50 px-2 py-1 text-[10px] tabular-nums text-slate-500">
        {new Date(item.timeStamp).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </div>
    </div>
  );
}
